/**
 * WakeWordListener — lightweight, browser-native "Hey ARIA" wake-word.
 *
 * Uses the Web Speech API (webkitSpeechRecognition) running continuously in the
 * background while the user is on the chat page. When a transcript contains a
 * trigger phrase ("hey aria", "hi aria", "okay aria", "aria"), we fire onWake.
 *
 * Designed to coexist with ConversationMode and the manual mic recorder: the
 * listener pauses itself whenever `paused` is true (conversation overlay open,
 * the chat-input mic is active, etc.) so we never fight for the microphone.
 *
 * Browser support: Chrome / Edge / Safari (16+). If unsupported we no-op and
 * report `unsupported: true` via onStatus so the UI can hide affordances.
 */
import { useEffect, useRef } from "react";

const TRIGGERS = [
  "hey aria", "hi aria", "okay aria", "ok aria",
  "hey area", "hi area",          // common Whisper / Web Speech mishears
  "hey ariel", "hey arya",
];

// Match: phrase appears anywhere in the most recent 40 chars of the rolling
// transcript. We require a word boundary so "malaria" doesn't trigger.
function detectTrigger(text) {
  if (!text) return false;
  const lc = text.toLowerCase().slice(-60);
  return TRIGGERS.some(t => new RegExp(`(^|[\\s,.!?'"-])${t}\\b`).test(lc));
}

export default function WakeWordListener({
  enabled = false,
  paused = false,
  onWake,
  onStatus,        // (state: "off"|"unsupported"|"denied"|"listening"|"error") => void
  cooldownMs = 3000,
}) {
  const recogRef = useRef(null);
  const wantRunningRef = useRef(false);
  const lastWakeAtRef = useRef(0);
  const restartTimerRef = useRef(null);
  const startedRef = useRef(false);  // tracks whether recognition is currently started

  // Stable refs to callbacks so we don't recreate recognition on every render
  const onWakeRef = useRef(onWake);
  const onStatusRef = useRef(onStatus);
  useEffect(() => { onWakeRef.current = onWake; }, [onWake]);
  useEffect(() => { onStatusRef.current = onStatus; }, [onStatus]);

  useEffect(() => {
    const SR = typeof window !== "undefined" &&
      (window.SpeechRecognition || window.webkitSpeechRecognition);

    if (!SR) {
      onStatusRef.current?.("unsupported");
      return;
    }

    // Only one recognition instance for the lifetime of this component
    const recog = new SR();
    recog.continuous = true;
    recog.interimResults = true;
    recog.lang = "en-US";
    recog.maxAlternatives = 1;

    recog.onresult = (event) => {
      // Walk only the freshest results; combining everything quickly bloats
      let latest = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        latest += event.results[i][0].transcript + " ";
      }
      if (!detectTrigger(latest)) return;

      const now = Date.now();
      if (now - lastWakeAtRef.current < cooldownMs) return;
      lastWakeAtRef.current = now;

      try { onWakeRef.current?.(latest.trim()); } catch (e) { /* swallow */ }
    };

    recog.onerror = (e) => {
      // Common: "no-speech", "audio-capture", "not-allowed", "aborted"
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        wantRunningRef.current = false;
        onStatusRef.current?.("denied");
        return;
      }
      // network / no-speech / aborted: silently restart via onend handler
    };

    recog.onend = () => {
      startedRef.current = false;
      if (!wantRunningRef.current) {
        onStatusRef.current?.("off");
        return;
      }
      // Auto-restart with a small backoff (browsers stop continuous after ~60s)
      if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
      restartTimerRef.current = setTimeout(() => {
        if (!wantRunningRef.current) return;
        try {
          recog.start();
          startedRef.current = true;
          onStatusRef.current?.("listening");
        } catch (err) {
          // InvalidStateError → already running; ignore
          onStatusRef.current?.("error");
        }
      }, 350);
    };

    recogRef.current = recog;
    return () => {
      wantRunningRef.current = false;
      if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
      try { recog.onend = null; recog.onerror = null; recog.onresult = null; } catch (err) { console.error("[ARIA]", err); }
      try { recog.abort(); } catch (err) { console.error("[ARIA]", err); }
      recogRef.current = null;
      startedRef.current = false;
    };
  }, [cooldownMs]);

  // Start/stop based on enabled & paused
  useEffect(() => {
    const recog = recogRef.current;
    if (!recog) return;
    const shouldRun = enabled && !paused;
    wantRunningRef.current = shouldRun;

    if (shouldRun && !startedRef.current) {
      try {
        recog.start();
        startedRef.current = true;
        onStatusRef.current?.("listening");
      } catch (e) {
        // InvalidStateError: already started. Safe to ignore.
      }
    } else if (!shouldRun && startedRef.current) {
      try { recog.stop(); } catch (err) { console.error("[ARIA]", err); }
      // onend will fire and set startedRef → false
      onStatusRef.current?.("off");
    }
  }, [enabled, paused]);

  return null;
}
